import type { CreditBillingTimelineSummary } from '@nadoutong/contracts';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CUSTOMER_READ_ROLES, hasAnyOpsRole } from '../../auth/opsRoles.ts';
import { DualCurrencyAmount } from '../../components/DualCurrencyAmount.tsx';
import { CreditPaymentSummaryLine } from '../../components/credit/CreditBillingTimeline.tsx';
import { formatDisplayRateHint, formatLocalDate } from '../../components/formatters.ts';
import { TemporaryCreditEvidenceDownload } from '../../components/TemporaryCreditEvidenceDownload.tsx';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';

/**
 * Temporary-credit approval detail (router#2000). Finance signs a
 * remittance-bridge grant; the payload carries the expected arrival and
 * the payment evidence (text and/or an attachment key). No A/B/C/D
 * grade — the signer reads the evidence as-is.
 */

interface TemporaryCreditDetailProps {
  readonly orgId: string;
  readonly requestedLimitUsdMicros: number | null;
  readonly requestedLimitCnyCents: number;
  readonly expectedArrivalAmountUsdMicros: number | null;
  readonly expectedArrivalAmountCnyCents: number;
  readonly expectedArrivalDate: string;
  readonly appliedRate?: number | null;
  readonly paymentEvidenceText?: string;
  readonly paymentEvidenceAttachmentKey?: string;
  readonly registeredAt?: string | null;
  readonly paymentSummary?: CreditBillingTimelineSummary | null;
}

export function TemporaryCreditDetail({
  orgId,
  requestedLimitUsdMicros,
  requestedLimitCnyCents,
  expectedArrivalAmountUsdMicros,
  expectedArrivalAmountCnyCents,
  expectedArrivalDate,
  appliedRate = null,
  paymentEvidenceText,
  paymentEvidenceAttachmentKey,
  registeredAt = null,
  paymentSummary = null,
}: TemporaryCreditDetailProps): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const mayOpenCustomer360 = hasAnyOpsRole(user?.roles ?? [], CUSTOMER_READ_ROLES);

  return (
    <div className="flex flex-col gap-3" data-testid={`approval-temporary-credit-detail-${orgId}`}>
      <dl className="grid gap-2 sm:grid-cols-4">
        <div>
          <dt className="text-label text-paper-low">{t('credit.temporary.detail.limit')}</dt>
          <dd className="font-mono text-dense text-paper">
            <DualCurrencyAmount
              usdMicros={requestedLimitUsdMicros}
              cnyCents={requestedLimitCnyCents}
              signed={false}
              align="start"
            />
          </dd>
        </div>
        <div>
          <dt className="text-label text-paper-low">{t('credit.temporary.detail.expected_amount')}</dt>
          <dd className="font-mono text-dense text-paper">
            <DualCurrencyAmount
              usdMicros={expectedArrivalAmountUsdMicros}
              cnyCents={expectedArrivalAmountCnyCents}
              signed={false}
              align="start"
            />
          </dd>
        </div>
        <div>
          <dt className="text-label text-paper-low">{t('credit.temporary.detail.expected_date')}</dt>
          <dd className="font-mono text-dense text-paper">{expectedArrivalDate}</dd>
        </div>
        <div>
          <dt className="text-label text-paper-low">{t('credit.temporary.detail.applied_rate')}</dt>
          <dd
            className="font-mono text-dense text-paper"
            data-testid="approval-temporary-credit-applied-rate"
          >
            {formatDisplayRateHint(appliedRate)}
          </dd>
        </div>
        <div>
          <dt className="text-label text-paper-low">{t('approvals.credit.registered_at')}</dt>
          <dd
            className="font-mono text-dense text-paper"
            data-testid="approval-temporary-credit-registered-at"
          >
            {registeredAt === null ? t('credit.overview.dash') : formatLocalDate(registeredAt)}
          </dd>
        </div>
      </dl>
      {paymentSummary === null ? null : (
        <div>
          <dt className="text-label text-paper-low">{t('approvals.credit.payment_summary')}</dt>
          <dd data-testid="approval-temporary-credit-payment-summary">
            <CreditPaymentSummaryLine summary={paymentSummary} />
          </dd>
        </div>
      )}
      {paymentEvidenceText !== undefined ? (
        <p className="text-dense text-paper" data-testid="approval-temporary-credit-evidence-text">
          {paymentEvidenceText}
        </p>
      ) : null}
      {paymentEvidenceAttachmentKey !== undefined ? (
        <TemporaryCreditEvidenceDownload
          orgId={orgId}
          evidenceAttachmentKey={paymentEvidenceAttachmentKey}
          testId="approval-temporary-credit-evidence"
        />
      ) : null}
      {mayOpenCustomer360 ? (
        <Link
          to={`/crm/customers/${encodeURIComponent(orgId)}?tab=credit`}
          className="inline-flex w-fit items-center gap-1.5 text-dense text-signal hover:underline"
          data-testid="approval-temporary-credit-customer-360-link"
        >
          <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
          {t('approvals.credit.open_customer_360')}
        </Link>
      ) : null}
    </div>
  );
}
